// src/v3/ui/_common/components/ui/job-run-form.tsx

import { Loader2, Play } from "lucide-react";
import { useState } from "react";
import { JOB_PARAM_TYPE_OBJ, type TJobParamType } from "../../../_deps";
import { t } from "../../../locales";
import { Button } from "./button";
import { Select } from "./select";

export type JobParamField = {
  name: string;
  type: TJobParamType;
  required: boolean;
  options?: string[] | undefined;
  description?: string | undefined;
  defaultValue?: boolean | undefined;
  label?: string | undefined;
};

interface JobRunFormProps {
  fields: JobParamField[];
  onRun: (params: Record<string, unknown>) => Promise<void>;
  isRunning: boolean;
}

export function JobRunForm({ fields, onRun, isRunning }: JobRunFormProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [multiValues, setMultiValues] = useState<Record<string, string[]>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): Record<string, unknown> | null => {
    const newErrors: Record<string, string> = {};
    const result: Record<string, unknown> = {};
    for (const field of fields) {
    if (field.type === JOB_PARAM_TYPE_OBJ.BOOLEAN) {
        const checked =
          values[field.name] === "true" ||
          (values[field.name] === undefined && field.defaultValue === true);
        result[field.name] = checked;
        continue;
      }
      if (field.type === JOB_PARAM_TYPE_OBJ.MULTISELECT) {
        const selected = multiValues[field.name] ?? [];
        if (field.required && selected.length === 0) {
          newErrors[field.name] = t("scheduler.params.required");
          continue;
        }
        if (selected.length > 0) {
          result[field.name] = selected.join(",");
        }
        continue;
      }
      const v = values[field.name] ?? "";
      if (field.required && v.trim() === "") {
        newErrors[field.name] = t("scheduler.params.required");
        continue;
      }
      if (v.trim() === "") continue;
    if (field.type === JOB_PARAM_TYPE_OBJ.DATE && !/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        newErrors[field.name] = t("scheduler.params.invalidDate");
        continue;
      }
    if (field.type === JOB_PARAM_TYPE_OBJ.NUMBER) {
        const n = Number(v);
        if (!Number.isFinite(n)) {
          newErrors[field.name] = t("scheduler.params.invalidNumber");
          continue;
        }
        result[field.name] = n;
        continue;
      }
      result[field.name] = v;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? result : null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = validate();
    if (!valid) return;
    await onRun(valid);
  };

  const toggleMulti = (fieldName: string, opt: string) => {
    setMultiValues((prev) => {
      const current = prev[fieldName] ?? [];
      return {
        ...prev,
        [fieldName]: current.includes(opt)
          ? current.filter((o) => o !== opt)
          : [...current, opt],
      };
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-2 space-y-2 rounded-md border bg-muted/30 p-3"
    >
      {fields.map((field) => (
        <div key={field.name} className="space-y-1">
          <label
            className="text-xs font-medium"
            htmlFor={`param-${field.name}`}
          >
            {field.label ?? field.name}
            {field.required && <span className="text-red-500"> *</span>}
          </label>
          {field.type === JOB_PARAM_TYPE_OBJ.ENUM && field.options ? (
            <Select
              size="sm"
              value={values[field.name] ?? ""}
              onChange={(v) =>
                setValues((prev) => ({
                  ...prev,
                  [field.name]: v,
                }))
              }
              ariaLabel={field.name}
              className="w-full"
              options={[
                {
                  value: "",
                  label: t("scheduler.params.selectPlaceholder"),
                },
                ...field.options.map((opt) => ({
                  value: opt,
                  label: opt,
                })),
              ]}
            />
            ) : field.type === JOB_PARAM_TYPE_OBJ.BOOLEAN ? (
            <label className="flex items-center gap-2">
              <input
                id={`param-${field.name}`}
                type="checkbox"
                checked={
                  values[field.name] === "true" ||
                  (field.defaultValue === true &&
                    values[field.name] === undefined)
                }
                onChange={(e) =>
                  setValues((prev) => ({
                    ...prev,
                    [field.name]: e.target.checked ? "true" : "false",
                  }))
                }
                className="h-4 w-4 rounded border-input"
                aria-label={field.name}
              />
              <span className="text-xs text-muted-foreground">
                {field.description ?? field.name}
              </span>
            </label>
            ) : field.type === JOB_PARAM_TYPE_OBJ.NUMBER ? (
            <input
              id={`param-${field.name}`}
              type="number"
              value={values[field.name] ?? ""}
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  [field.name]: e.target.value,
                }))
              }
              className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
              placeholder={field.name}
              aria-label={field.name}
            />
            ) : field.type === JOB_PARAM_TYPE_OBJ.MULTISELECT && field.options ? (
            <div className="flex flex-wrap gap-2">
              {field.options.map((opt) => {
                const selected = (multiValues[field.name] ?? []).includes(opt);
                return (
                  <label
                    key={opt}
                    className="flex items-center gap-1 rounded border px-2 py-1 text-xs"
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleMulti(field.name, opt)}
                      className="h-3 w-3 rounded border-input"
                      aria-label={opt}
                    />
                    {opt}
                  </label>
                );
              })}
            </div>
          ) : (
            <input
              id={`param-${field.name}`}
            type={field.type === JOB_PARAM_TYPE_OBJ.DATE ? "date" : "text"}
              value={values[field.name] ?? ""}
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  [field.name]: e.target.value,
                }))
              }
              className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-sm"
            placeholder={field.type === JOB_PARAM_TYPE_OBJ.DATE ? "YYYY-MM-DD" : field.name}
              aria-label={field.name}
            />
          )}
          {errors[field.name] && (
            <p className="text-xs text-red-500">{errors[field.name]}</p>
          )}
        </div>
      ))}
      <Button type="submit" size="sm" disabled={isRunning}>
        {isRunning ? (
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
        ) : (
          <Play className="mr-1 h-3 w-3" />
        )}
        {t("scheduler.run")}
      </Button>
    </form>
  );
}
